import { useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
} from "@mui/material";
import axios from "axios.js";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DataTable from "theme/Tables/DataTable";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";

function CampaignViewsCard({ campaignRef, triggerRefresh, campaignViews }) {
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [tableRows, setTableRows] = useState([]);
  const [campaignViewEmail, setCampaignViewEmail] = useState(null);
  const [campaignViewAccessType, setCampaignViewAccessType] = useState(null);
  const [openCreateCampaignViewDialog, setOpenCreateCampaignViewDialog] = useState(false);

  async function removeCampaignView(ref) {
    setShowLoading(true);
    await axios
      .get(`/portal/campaigns/windows/${ref}/delete.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const campaignViewTableColumns = [
    { Header: "Email", accessor: "email", align: "left" },
    { Header: "Access Type", accessor: "access_type", align: "left" },
    { Header: "Link", accessor: "link", align: "left" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildCampaignViewsRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        // title: row.title,
        email: row.email_address,
        access_type: row.access_type,
        link: (
          <a href={`${row.link}`} target="_blank" rel="noreferrer">
            {row.link}
          </a>
        ),
        actions: (
          <MDBox mt={1} variant="button">
            <MDButton
              size="small"
              variant="outlined"
              color="error"
              iconOnly
              onClick={() => {
                if (window.confirm(`Remove access?`) === true) {
                  removeCampaignView(row.reference);
                }
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                delete
              </Icon>
            </MDButton>
          </MDBox>
        ),
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const cpViews = buildCampaignViewsRows(campaignViews);
    setTableRows(cpViews);
  }, [campaignViews]);

  async function createCampaignViewAccess() {
    if (campaignViewEmail === null) {
      setSeverity("error");
      setErrMsg("Please enter a valid email address.");
      setOpen(true);
      return;
    }

    if (campaignViewAccessType === null) {
      setSeverity("error");
      setErrMsg("Please select an access type.");
      setOpen(true);
      return;
    }

    if (window.confirm("Are you sure you want to proceed with this action?") === false) {
      return;
    }

    setOpenCreateCampaignViewDialog(false);
    setShowLoading(true);
    const json = {
      campaign: campaignRef,
      email_address: campaignViewEmail,
      access_type: campaignViewAccessType,
    };

    await axios
      .post(`portal/campaigns/windows/create.json`, json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <MDBox>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openCreateCampaignViewDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Share Campaign Access</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            <MDBox mt={0}>
              <MDInput
                name="email"
                margin="dense"
                label="Email Address"
                type="email"
                value={campaignViewEmail}
                fullWidth
                variant="outlined"
                onChange={(e) => setCampaignViewEmail(e.target.value)}
              />
            </MDBox>
            <MDBox mt={3}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Access Type</InputLabel>
                <Select
                  label="Access Type"
                  onChange={(e) => {
                    setCampaignViewAccessType(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                  value={campaignViewAccessType}
                >
                  <MenuItem value="VIEW">View Only</MenuItem>
                  <MenuItem value="DOWNLOAD">View & Download</MenuItem>
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mt={3} px={1}>
              <Alert severity="info">
                <MDTypography variant="body1" fontSize="small" justifyContent="space-evenly">
                  A unique access link will be sent to the email address provided above.
                </MDTypography>
              </Alert>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenCreateCampaignViewDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              createCampaignViewAccess();
            }}
          >
            Send Access
          </MDButton>
        </DialogActions>
      </Dialog>
      <Card sx={{ height: "100%", marginTop: "0px" }}>
        <MDBox>
          <MDBox p={2}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h6" fontWeight="light">
                <b>3rd Party Access</b>
                <br />
                <small>
                  Grant third parties controlled access to insights, reports and agent performances.
                </small>
              </MDTypography>
              <MDBox display="flex" justifyContent="space-evenly" alignItems="end">
                <MDBox>
                  <MDButton
                    variant="contained"
                    color="dark"
                    iconOnly
                    onClick={() => {
                      setCampaignViewEmail(null);
                      setOpenCreateCampaignViewDialog(true);
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          </MDBox>
          <MDBox p={2}>
            <DataTable
              table={{ columns: campaignViewTableColumns, rows: tableRows }}
              isSorted
              canSearch={false}
              entriesPerPage={false}
              showTotalEntries
              buttons
              noEndBorder
            />
          </MDBox>
        </MDBox>
      </Card>
    </MDBox>
  );
}

export default CampaignViewsCard;
